import {
  ApolloError,
  useMutation,
  useQuery,
  useSuspenseQuery,
} from "@apollo/client";
import {
  AccountList,
  Alert,
  AssetCard,
  BreadcrumbArray,
  Button,
  Card,
  ConnectedAnnotatedField,
  Copyable,
  CurrencyAmountUI,
  CurrencyFlagEmoji,
  cn,
  DataTable,
  DateUI,
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  ErrorTooltip,
  Heading,
  Icon,
  Label,
  LoadingToastProvider,
  MfaFlow,
  NumericFormatInput,
  PrimaryFullScreenSpinner,
  Spinner,
  Switch,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  Txt,
  toast,
  UserProfile,
  useAppForm,
} from "@repo/ui";
import {
  arrayBufferToBase64,
  dateSchema,
  decimalSchema,
  NotFoundError,
  neverFunc,
  prettyPrintEnum,
} from "@repo/utils";
import {
  createFileRoute,
  Link,
  useNavigate,
  useParams,
} from "@tanstack/react-router";
import type { ColumnDef } from "@tanstack/react-table";
import { DateTime } from "luxon";
import { type ReactNode, Suspense, useRef, useState } from "react";
import { FaInfoCircle } from "react-icons/fa";
import {
  PiBuildingOffice,
  PiCheckCircle,
  PiClipboard,
  PiNotePencil,
  PiPlus,
  PiRobot,
  PiUser,
} from "react-icons/pi";
import {
  RiArrowDownSLine,
  RiBankLine,
  RiExternalLinkLine,
} from "react-icons/ri";
import { z } from "zod";
import loadingAnimation from "~/assets/animations/loading.riv";
import { AdminPageContainer } from "~/components/layout/AdminPageContainer";
import { graphql } from "~/gql/client";
import {
  activityStatementFormatValues,
  currencyValues,
  organisationApprovalPolicyKindValues,
  organisationFeePaymentMethodValues,
  userMfaRequirementValues,
} from "~/gql/client/enumValues";
import type {
  AccountsGroupKindInput,
  AccountsSummaryFilter,
  AccountsSummaryOrderBy,
  ActivityStatementFormat,
  AdminOrganisationApprovalPolicyFieldsFragment,
  AdminOrganisationQuery,
  OrgAssetRuleRowFragment,
  OrganisationApprovalPolicyKind,
  UpdateOrganisationDetailInput,
} from "~/gql/client/graphql";
import { opsHead, useOpsTitle } from "~/head";

export const Route = createFileRoute("/__auth/organisations_/$organisationId")({
  head: () => opsHead("Organisation"),
  component: () => (
    <Suspense fallback={<PrimaryFullScreenSpinner />}>
      <OrganisationLayout />
    </Suspense>
  ),
});

const assetRuleColumns: ColumnDef<OrgAssetRuleRowFragment>[] = [
  {
    id: "account",
    header: "Account",
    cell: ({ row }) =>
      row.original.asset ? (
        <AssetCard
          asset={row.original.asset}
          variant="BUSINESS_ENTITY"
          linkTo={`/assets/${row.original.asset.id}`}
        />
      ) : (
        "—"
      ),
  },
  {
    id: "type",
    header: "Type",
    cell: ({ row }) => {
      switch (row.original.__typename) {
        case "TopUpAssetRule":
          return "Top-up";
        case "AlertAssetRule":
          return "Alert";
        case "RecurringWithdrawalAssetRule":
          return "Recurring withdrawal";
        default:
          return neverFunc(row.original);
      }
    },
  },
  {
    id: "amount",
    header: "Amount",
    cell: ({ row }) => {
      const rule = row.original;
      if (rule.__typename === "TopUpAssetRule" && rule.balanceTarget) {
        return (
          <div className="flex flex-col">
            <Txt size="xs" color="secondary">
              Target
            </Txt>
            <CurrencyAmountUI
              currency={rule.balanceTarget.currency}
              amount={rule.balanceTarget.amount}
            />
          </div>
        );
      }
      if (rule.__typename === "AlertAssetRule") {
        return (
          <div className="flex flex-col gap-0.5">
            {rule.minimumBalanceThreshold && (
              <div className="flex flex-col">
                <Txt size="xs" color="secondary">
                  Min
                </Txt>
                <CurrencyAmountUI
                  currency={rule.minimumBalanceThreshold.currency}
                  amount={rule.minimumBalanceThreshold.amount}
                />
              </div>
            )}
            {rule.maximumBalanceThreshold && (
              <div className="flex flex-col">
                <Txt size="xs" color="secondary">
                  Max
                </Txt>
                <CurrencyAmountUI
                  currency={rule.maximumBalanceThreshold.currency}
                  amount={rule.maximumBalanceThreshold.amount}
                />
              </div>
            )}
          </div>
        );
      }
      if (rule.__typename === "RecurringWithdrawalAssetRule" && rule.amount) {
        return (
          <CurrencyAmountUI
            currency={rule.amount.currency}
            amount={rule.amount.amount}
          />
        );
      }
      return "—";
    },
  },
  {
    id: "status",
    header: "Status",
    cell: ({ row }) =>
      row.original.evaluationStatus
        ? prettyPrintEnum(row.original.evaluationStatus)
        : "—",
  },
  {
    id: "cadence",
    header: "Cadence",
    cell: ({ row }) => prettyPrintEnum(row.original.evaluationCadence),
  },
  {
    id: "nextEvaluation",
    header: "Next evaluation",
    cell: ({ row }) => <DateUI date={row.original.nextEvaluationDueAt} />,
  },
];

const approvalPolicyFormSchema = z.object({
  kind: z.enum(organisationApprovalPolicyKindValues),
  numUserReviewsRequired: z.enum(["0", "1", "2", "3", "4"] as const, {
    error: "Select a value between 0 and 4",
  }),
  allowSelfReview: z.boolean(),
  allowPolicyOverride: z.boolean(),
});

type ApprovalPolicyFormValues = z.input<typeof approvalPolicyFormSchema>;

type ApprovalPolicy = {
  kind: OrganisationApprovalPolicyKind;
  approvalPolicy:
    | AdminOrganisationApprovalPolicyFieldsFragment
    | null
    | undefined;
};

const makeFormSchema = (
  organisation: NonNullable<AdminOrganisationQuery["organisation"]>,
) =>
  z
    .object({
      activityStatementFormat: z.enum(activityStatementFormatValues),
      fixedMonthlyFeeAmount: decimalSchema.optional(),
      fxMarginRateBps: decimalSchema.optional(),
      stripePayoutFeeRate: decimalSchema.nullish(),
      fixedMonthlyFeeCurrency: z.enum(currencyValues).optional(),
      fixedMonthlyFeeContractEndDate: dateSchema.optional(),
      userMfaRequirement: z.enum(userMfaRequirementValues),
      homeCurrency: z.enum(currencyValues),
      xeroContactId: z.string(),
      feePaymentMethod: z.enum(organisationFeePaymentMethodValues),
      longLogo: z
        .instanceof(File)
        .nullish()
        .refine((file) => !file || file.type === "image/png", {
          message: "Only PNG files are allowed",
        })
        .refine((file) => !file || file.size <= 2 * 1024 * 1024, {
          message: "File must be less than 2MB",
        }),
    })
    .superRefine((data, ctx) => {
      if (data.fixedMonthlyFeeAmount && !data.fixedMonthlyFeeCurrency) {
        ctx.addIssue({
          path: ["fixedMonthlyFeeCurrency"],
          code: z.ZodIssueCode.custom,
          message: "Currency is required",
        });
      }

      if (!data.fixedMonthlyFeeAmount && data.fixedMonthlyFeeCurrency) {
        ctx.addIssue({
          path: ["fixedMonthlyFeeAmount"],
          code: z.ZodIssueCode.custom,
          message: "Amount is required",
        });
      }
    })
    .superRefine((value, ctx) => {
      if (value.activityStatementFormat !== "NETSUITE") {
        return;
      }

      const businessEntitiesMissingNetsuiteIds =
        organisation.businessEntities?.filter(
          (entity) => !entity.netsuiteInternalCustomerId?.trim(),
        );

      if (
        businessEntitiesMissingNetsuiteIds &&
        businessEntitiesMissingNetsuiteIds?.length > 0
      ) {
        ctx.addIssue({
          path: ["activityStatementFormat"],
          code: z.ZodIssueCode.custom,
          message: `You must set a NetSuite ID on all business entities in this organisation. ${businessEntitiesMissingNetsuiteIds.map(({ name }) => name).join(", ")} ${businessEntitiesMissingNetsuiteIds.length === 1 ? "is" : "are"} currently missing ${businessEntitiesMissingNetsuiteIds.length === 1 ? "a NetSuite ID" : "NetSuite IDs"}.`,
        });
      }
    });

type FormSchema = z.input<ReturnType<typeof makeFormSchema>>;

const now = DateTime.now();
const currentMonthStart = now.startOf("month").toJSDate();
const previousMonthStart = now.startOf("month").minus({ month: 1 }).toJSDate();
const previousMonthEnd = now
  .startOf("month")
  .minus({ month: 1 })
  .endOf("month")
  .toJSDate();
const yieldSince = now.minus({ days: 30 }).toJSDate();

function OrganisationLayout() {
  const params = useParams({ strict: false });
  const { organisationId } = params;
  if (!organisationId) {
    throw new NotFoundError();
  }

  const { data } = useSuspenseQuery(
    graphql(`
      query AdminOrganisation($id: ID!) {
          organisation(id: $id) {
              id
              shortRef
              name
              activityStatementFormat
              estimatedValue {
                  currency
                  amount
              }
              homeCurrency
              createdAt
              squareLogoUrl
              fixedMonthlyFee {
                  currency
                  amount
              }
              fixedMonthlyFeeContractEndDate
              userMfaRequirement
              hasLongLogo
              longLogoDataUrl
              xeroContact {
                  id
              }
              businessEntities {
                id
                name
                currency
                netsuiteInternalCustomerId
                assets(includeInactive: true) {
                  id
                  active
                  ...AssetCardFields
                }
              }
              feePaymentMethod
              fxMarginRateBps
              stripePayoutFeeRate
          payoutApprovalPolicy {
            ...AdminOrganisationApprovalPolicyFields
          }
          transferApprovalPolicy {
            ...AdminOrganisationApprovalPolicyFields
          }
          thirdPartyPaymentApprovalPolicy {
            ...AdminOrganisationApprovalPolicyFields
          }
          memberships {
            id
            userProfile {
              id
              ...UserProfileUI
              notificationsEnabled
            }
          }
          assetRulesSummary {
            id
            activeAssetRules {
              ...OrgAssetRuleRow
            }
          }
        }
      }

      fragment OrgAssetRuleRow on AssetRule {
        __typename
        id
        evaluationCadence
        evaluationStatus
        nextEvaluationDueAt
        asset {
          id
          ...AssetCardFields
        }
        ... on TopUpAssetRule {
          balanceTarget {
            currency
            amount
          }
        }
        ... on AlertAssetRule {
          minimumBalanceThreshold {
            currency
            amount
          }
          maximumBalanceThreshold {
            currency
            amount
          }
        }
        ... on RecurringWithdrawalAssetRule {
          amount {
            currency
            amount
          }
        }
      }

      fragment AdminOrganisationApprovalPolicyFields on ApprovalPolicy {
        id
        allowPolicyOverride
        allowSelfReview
        numUserReviewsRequired
      }
  `),
    {
      variables: {
        id: organisationId,
      },
    },
  );

  const organisation = data.organisation;
  if (!organisation) {
    throw new NotFoundError();
  }

  useOpsTitle(`${organisation.name} - Organisation`);

  const [includeInactiveAssets, setIncludeInactiveAssets] = useState(false);
  const [editDetailsCollapsed, setEditDetailsCollapsed] = useState(true);
  const [businessEntitiesCollapsed, setBusinessEntitiesCollapsed] =
    useState(false);
  const [usersCollapsed, setUsersCollapsed] = useState(true);
  const notificationLoadingUserIdRef = useRef<string | null>(null);

  const [approvalsCollapsed, setApprovalsCollapsed] = useState(true);
  const [rulesCollapsed, setRulesCollapsed] = useState(true);
  const [accountsFilter, setAccountsFilter] = useState<AccountsSummaryFilter>({
    includeInactive: includeInactiveAssets,
  });
  const [accountsOrderBy, setAccountsOrderBy] = useState<
    AccountsSummaryOrderBy[] | undefined
  >([{ balance: "DESC" }]);
  const [accountsGroup, setAccountsGroup] = useState<
    AccountsGroupKindInput | undefined
  >("ACCOUNT_TYPE");

  const {
    data: accountListData,
    previousData: previousAccountListData,
    loading: accountListLoading,
  } = useQuery(
    graphql(`
      query AdminOrganisationAccounts(
          $id: ID!
          $yieldSince: DateTime!
          $currentMonthStart: DateTime!
          $previousMonthStart: DateTime!
          $previousMonthEnd: DateTime!
          $filter: AccountsSummaryFilter!
          $group: AccountsGroupKindInput!
          $skipGroup: Boolean!
          $skipYieldAccrued: Boolean!
          $orderBy: [AccountsSummaryOrderBy!]
      ) {
          organisation(id: $id) {
              id
              ...AccountListOrganisation
              accountsSummary(filter: $filter, orderBy: $orderBy) {
                  ...AccountListAccountsSummary
              }
          }
      }
    `),
    {
      variables: {
        id: organisationId,
        yieldSince,
        currentMonthStart,
        previousMonthStart,
        previousMonthEnd,
        filter: accountsFilter,
        group: accountsGroup ?? "BUSINESS_ENTITY",
        skipGroup: !accountsGroup,
        skipYieldAccrued: false,
        orderBy: accountsOrderBy,
      },
    },
  );

  const accountListOrganisation =
    accountListData?.organisation ?? previousAccountListData?.organisation;
  const accountListSummary =
    accountListData?.organisation?.accountsSummary ??
    previousAccountListData?.organisation?.accountsSummary;

  const defaultValues: FormSchema = {
    activityStatementFormat: organisation.activityStatementFormat ?? "XERO",
    userMfaRequirement: organisation.userMfaRequirement,
    fixedMonthlyFeeAmount: organisation.fixedMonthlyFee?.amount,
    fxMarginRateBps: organisation.fxMarginRateBps,
    stripePayoutFeeRate: organisation.stripePayoutFeeRate ?? null,
    fixedMonthlyFeeCurrency: organisation.fixedMonthlyFee?.currency,
    fixedMonthlyFeeContractEndDate:
      organisation.fixedMonthlyFeeContractEndDate ?? undefined,
    homeCurrency: organisation.homeCurrency,
    xeroContactId: organisation.xeroContact?.id ?? "",
    feePaymentMethod: organisation.feePaymentMethod,
  };
  const navigate = useNavigate();

  const [updateOrganisationDetail, { loading }] = useMutation(
    graphql(`
      mutation UpdateOrganisationDetail($input: UpdateOrganisationDetailInput!) {
          updateOrganisationDetail(input: $input) {
              organisation {
                  id
                  activityStatementFormat
                  userMfaRequirement
                  homeCurrency
                  fixedMonthlyFeeContractEndDate
                  fixedMonthlyFee {
                      currency
                      amount
                  }
                  hasLongLogo
                  feePaymentMethod
                  xeroContact {
                      id
                  }
                  fxMarginRateBps
                  stripePayoutFeeRate
              }
          }
      }
  `),
  );

  const [deactivateOrganisation, { loading: deactivateLoading }] = useMutation(
    graphql(`
      mutation DeactivateOrganisation($input: DeactivateOrganisationInput!) {
        deactivateOrganisation(input: $input) {
          organisation {
            id
          }
        }
      }
    `),
  );
  const [setOrganisationLongLogo] = useMutation(
    graphql(`
      mutation SetOrganisationLongLogo($input: SetOrganisationLongLogoInput!) {
        setOrganisationLongLogo(input: $input) {
          organisation {
            id
            hasLongLogo
            longLogoDataUrl
          }
        }
      }
    `),
  );

  const [
    setUserNotificationsEnabled,
    { loading: notificationToggleLoading, data: notificationData },
  ] = useMutation(
    graphql(`
      mutation SetUserNotificationsEnabled(
        $input: SetUserNotificationsEnabledInput!
      ) {
        setUserNotificationsEnabled(input: $input) {
          userProfile {
            id
            notificationsEnabled
          }
        }
      }
    `),
  );

  const formSchema = makeFormSchema(organisation);
  const form = useAppForm({
    defaultValues,
    validators: {
      onSubmit: formSchema,
    },
    onSubmit: async ({ value }) => {
      const parsed = formSchema.safeParse(value);
      if (!parsed.success) {
        toast({
          kind: "error",
          title: "Error updating details",
        });
        return;
      }

      const input: UpdateOrganisationDetailInput = {
        organisationId: organisation.id,
        activityStatementFormat: parsed.data.activityStatementFormat,
        userMfaRequirement: parsed.data.userMfaRequirement,
        homeCurrency: parsed.data.homeCurrency,
        fixedMonthlyFeeContractEndDate:
          parsed.data.fixedMonthlyFeeContractEndDate,
        xeroContactId: parsed.data.xeroContactId,
        feePaymentMethod: parsed.data.feePaymentMethod,
        fxMarginRateBps: parsed.data.fxMarginRateBps,
        stripePayoutFeeRate: parsed.data.stripePayoutFeeRate,
      };
      if (
        parsed.data.fixedMonthlyFeeAmount &&
        parsed.data.fixedMonthlyFeeCurrency
      ) {
        input.fixedMonthlyFee = {
          amount: parsed.data.fixedMonthlyFeeAmount,
          currency: parsed.data.fixedMonthlyFeeCurrency,
        };
      }

      try {
        await Promise.all([
          updateOrganisationDetail({
            variables: {
              input,
            },
          }),
          (async (): Promise<void> => {
            if (parsed.data.longLogo === undefined) {
              return;
            }

            await setOrganisationLongLogo({
              variables: {
                input: {
                  organisationId: organisation.id,
                  logoLongPngOnLightBase64:
                    parsed.data.longLogo === null
                      ? null
                      : arrayBufferToBase64(
                          await parsed.data.longLogo.arrayBuffer(),
                        ),
                },
              },
            });
          })(),
        ]);

        toast({
          kind: "success",
          title: "Updated details",
        });
      } catch (e) {
        if (e instanceof ApolloError) {
          const errorMessage = e.graphQLErrors?.[0]?.message;
          toast({
            kind: "error",
            title: "Error updating details",
            description: errorMessage,
          });
        } else {
          toast({
            kind: "error",
            title: "Error updating details",
          });
        }
      }
    },
  });

  const orgIdRef = useRef(organisation.id);
  if (organisation.id !== orgIdRef.current) {
    orgIdRef.current = organisation.id;
    form.reset(defaultValues);
  }

  const [previewUrl, setPreviewUrl] = useState<string | null>(
    organisation.longLogoDataUrl ?? null,
  );

  const longLogoRef = useRef<HTMLInputElement | null>(null);

  const handleDeactivateOrganisation = async () => {
    try {
      await deactivateOrganisation({
        variables: {
          input: {
            organisationId: organisation.id,
          },
        },
      });
      toast({
        kind: "success",
        title: "Organisation deactivated",
      });
      navigate({ to: "/organisations" });
    } catch (e) {
      if (e instanceof ApolloError) {
        const errorMessage = e.graphQLErrors?.[0]?.message;
        toast({
          kind: "error",
          title: "Error deactivating organisation",
          description: errorMessage,
        });
      } else {
        toast({
          kind: "error",
          title: "Error deactivating organisation",
        });
      }
    }
  };

  const policyDefinitions: ApprovalPolicy[] = [
    {
      kind: "PAYOUTS",
      approvalPolicy: data?.organisation?.payoutApprovalPolicy ?? null,
    },
    {
      kind: "TRANSFERS",
      approvalPolicy: data?.organisation?.transferApprovalPolicy ?? null,
    },
    {
      kind: "THIRD_PARTY_PAYMENTS",
      approvalPolicy:
        data?.organisation?.thirdPartyPaymentApprovalPolicy ?? null,
    },
  ];

  return (
    <AdminPageContainer>
      <div className="mt-8 flex flex-col gap-4">
        <Heading
          as="h2"
          size="2xl"
          className="mb-6 flex flex-row items-center gap-2 font-semibold"
        >
          <Icon
            size="10"
            imageUrl={organisation.squareLogoUrl ?? undefined}
            fallbackIcon={<PiBuildingOffice />}
          />
          {organisation.name}
        </Heading>
        <div className="flex w-full flex-row gap-6">
          <Card className="flex-[1] p-8">
            <Heading
              as="h2"
              size="xl"
              className="mb-6 flex flex-row items-center gap-2 font-semibold"
            >
              <PiClipboard />
              Summary
            </Heading>
            <div className="-mb-1 flex flex-row items-center justify-between">
              <div className="flex flex-row items-center gap-2">
                <Txt as="p" className="font-medium">
                  Balance:
                </Txt>
                <Txt as="p">
                  {organisation.estimatedValue ? (
                    <CurrencyAmountUI
                      {...organisation.estimatedValue}
                      size="lg"
                    />
                  ) : (
                    "-"
                  )}
                </Txt>
              </div>
              <div className="flex flex-row items-center gap-2">
                <Txt as="p" className="font-medium">
                  Signup Date:
                </Txt>
                <DateUI date={organisation.createdAt} />
              </div>
              <div className="flex flex-row items-center gap-2">
                <Txt as="p" className="font-medium">
                  ID:
                </Txt>
                <Copyable copyText={organisation.id}>
                  <Txt as="span">{organisation.shortRef}</Txt>
                </Copyable>
              </div>
              <a
                href={`https://dashboard.clerk.com/apps/app_2OIhiTGXt0Rh2WXy9Le8kD1mdPp/instances/ins_2OLiK9VnjvFtkdoQBxkTrfIbgNI/organizations/${organisation.id}`}
                target="_blank"
              >
                <div className="flex flex-row items-center gap-2 underline">
                  Clerk Org
                  <RiExternalLinkLine />
                </div>
              </a>
            </div>
          </Card>
        </div>

        <Card className="p-8">
          <button
            className="flex w-full flex-row items-center justify-between gap-2 text-left"
            onClick={() => setBusinessEntitiesCollapsed((prev) => !prev)}
            aria-label="Toggle business entities"
          >
            <div className="flex items-center gap-2 font-semibold text-xl">
              <PiBuildingOffice />
              Business Entities
            </div>
            <div
              className={cn(
                "transition-transform duration-300",
                businessEntitiesCollapsed ? "rotate-0" : "rotate-180",
              )}
            >
              <RiArrowDownSLine className="size-5" />
            </div>
          </button>

          {!businessEntitiesCollapsed && (
            <>
              <div className="mt-4 flex w-full flex-row flex-wrap gap-4">
                {(data?.organisation?.businessEntities ?? []).map((be) => (
                  <Link
                    key={be.id}
                    to={`/organisations/${organisation.id}/business-entities/${be.id}`}
                  >
                    <Button variant="outline" className="gap-2">
                      <CurrencyFlagEmoji
                        className="rounded-xs"
                        currency={be.currency ?? organisation.homeCurrency}
                      />
                      <div>{be.name}</div>
                    </Button>
                  </Link>
                ))}
              </div>
              <div className="flex w-full flex-row justify-end">
                <NewBusinessEntityDialogTrigger
                  organisationName={data.organisation?.name ?? ""}
                  organisationId={data.organisation?.id ?? ""}
                  organisationActivityStatementFormat={
                    data.organisation?.activityStatementFormat ?? undefined
                  }
                  onCreated={(newId) =>
                    navigate({
                      to: "/organisations/$organisationId/business-entities/$businessEntityId",
                      params: {
                        organisationId: data.organisation?.id ?? "",
                        businessEntityId: newId,
                      },
                    })
                  }
                >
                  <Button
                    size="xs-link"
                    variant="link"
                    className="justify-start text-start"
                  >
                    <PiPlus className="me-2 size-[1em]" />
                    Add new business entity
                  </Button>
                </NewBusinessEntityDialogTrigger>
              </div>
            </>
          )}
        </Card>

        <Card className="flex-1 p-8">
          <button
            className="flex w-full flex-row items-center justify-between gap-2 text-left"
            onClick={() => setEditDetailsCollapsed((prev) => !prev)}
            aria-label="Toggle edit details"
          >
            <div className="flex items-center gap-2 font-semibold text-xl">
              <PiNotePencil />
              Edit details
            </div>
            <div
              className={cn(
                "transition-transform duration-300",
                editDetailsCollapsed ? "rotate-0" : "rotate-180",
              )}
            >
              <RiArrowDownSLine className="size-5" />
            </div>
          </button>
          {!editDetailsCollapsed && (
            <form.AppForm>
              <form
                className="mt-6 grid grid-cols-1 gap-6 md:grid-cols-3"
                onSubmit={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  void form.handleSubmit();
                }}
              >
                <div className="md:col-span-1">
                  <form.AppField name="homeCurrency">
                    {(field) => (
                      <field.SelectField label="Home currency">
                        {currencyValues.map((currency) => (
                          <field.SelectItem key={currency} value={currency}>
                            <span className="flex flex-row items-center gap-1">
                              <CurrencyFlagEmoji currency={currency} />
                              <Txt as="span" size="sm">
                                {currency}
                              </Txt>
                            </span>
                          </field.SelectItem>
                        ))}
                      </field.SelectField>
                    )}
                  </form.AppField>
                </div>

                <div className="md:col-span-1">
                  <form.AppField name="userMfaRequirement">
                    {(field) => (
                      <field.SelectField label="User MFA requirement">
                        {userMfaRequirementValues.map((v) => (
                          <field.SelectItem key={v} value={v}>
                            {(() => {
                              switch (v) {
                                case "NOT_REQUIRED":
                                  return "Not required";
                                case "REQUIRED":
                                  return "Required";
                                default:
                                  return neverFunc(v);
                              }
                            })()}
                          </field.SelectItem>
                        ))}
                      </field.SelectField>
                    )}
                  </form.AppField>
                </div>

                <div className="md:col-span-1">
                  <form.AppField name="longLogo">
                    {(field) => (
                      <ErrorTooltip
                        error={field.state.meta.errors.at(0)?.message}
                      >
                        <div className="flex flex-col gap-1">
                          <Label>Long logo</Label>
                          <Txt size="xs" className="text-secondary">
                            Recommended: in height 48px; no padding; transparent
                            bg.
                          </Txt>
                          <div className="mt-1 flex flex-row items-center justify-between gap-2">
                            {previewUrl ? (
                              <img
                                src={previewUrl}
                                className="h-12 rounded border border-zinc-200"
                              />
                            ) : (
                              <div className="h-12 w-32 rounded border border-zinc-200 bg-zinc-50" />
                            )}
                            <div className="flex flex-row gap-4">
                              <input
                                ref={longLogoRef}
                                className="hidden"
                                type="file"
                                accept="image/png"
                                onChange={(e) => {
                                  const file = e.target.files?.[0];
                                  if (!file) return;

                                  field.handleChange(file);
                                  setPreviewUrl(URL.createObjectURL(file));
                                }}
                              />
                              <Button
                                size="sm"
                                type="button"
                                variant="outline"
                                onClick={() => {
                                  longLogoRef.current?.click();
                                }}
                              >
                                {previewUrl ? "Replace" : "Add"}
                              </Button>
                              {previewUrl && (
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  type="button"
                                  className="text-error-500"
                                  onClick={(): void => {
                                    field.handleChange(null);
                                    setPreviewUrl(null);
                                  }}
                                >
                                  Remove
                                </Button>
                              )}
                            </div>
                          </div>
                        </div>
                      </ErrorTooltip>
                    )}
                  </form.AppField>
                </div>

                <div className="md:col-span-1">
                  <Heading as="span" type="field" className="pb-2">
                    Fixed monthly fee
                  </Heading>
                  <div className="flex flex-row gap-2">
                    <div className="h-7">
                      <form.AppField name="fixedMonthlyFeeCurrency">
                        {(field) => (
                          <field.SelectField placeholder={"AUD"}>
                            {currencyValues.map((currency) => (
                              <field.SelectItem key={currency} value={currency}>
                                {currency}
                              </field.SelectItem>
                            ))}
                          </field.SelectField>
                        )}
                      </form.AppField>
                    </div>
                    <div className="flex-grow">
                      <form.Subscribe
                        selector={(state) =>
                          state.values.fixedMonthlyFeeCurrency
                        }
                      >
                        {(currency) => (
                          <form.AppField name="fixedMonthlyFeeAmount">
                            {(field) => (
                              <field.CurrencyAmountField
                                align="start"
                                size="lg"
                                disabled={!currency}
                                min={0}
                                currency={currency || organisation.homeCurrency}
                              />
                            )}
                          </form.AppField>
                        )}
                      </form.Subscribe>
                    </div>
                  </div>
                </div>
                <div className="md:col-span-1">
                  <form.AppField name="fixedMonthlyFeeContractEndDate">
                    {(field) => (
                      <field.DatePickerField label="Fixed monthly fee contract end date" />
                    )}
                  </form.AppField>
                </div>

                <div className="md:col-span-1">
                  <form.AppField name="feePaymentMethod">
                    {(field) => (
                      <field.SelectField label="Fee payment method">
                        {organisationFeePaymentMethodValues.map((v) => (
                          <field.SelectItem key={v} value={v}>
                            {(() => {
                              switch (v) {
                                case "INVOICE":
                                  return "Invoice";
                                case "DIRECT_DEBIT":
                                  return "Direct debit from Macquarie CMA";
                                default:
                                  return neverFunc(v);
                              }
                            })()}
                          </field.SelectItem>
                        ))}
                      </field.SelectField>
                    )}
                  </form.AppField>
                </div>

                <div className="md:col-span-1">
                  <form.AppField name="fxMarginRateBps">
                    {(field) => (
                      <ConnectedAnnotatedField label="FX trade fee margin rate bps">
                        <NumericFormatInput
                          className="min-h-10"
                          thousandSeparator=","
                          decimalScale={0}
                          allowNegative={false}
                          onBlur={field.handleBlur}
                          value={field.state.value}
                          onValueChange={field.handleChange}
                          endAdornment={
                            <div className="flex items-center">bps</div>
                          }
                        />
                      </ConnectedAnnotatedField>
                    )}
                  </form.AppField>
                </div>

                <div className="md:col-span-1">
                  <form.AppField name="stripePayoutFeeRate">
                    {(field) => (
                      <field.PercentageField
                        label="Stripe payout fee rate"
                        scale="0-1"
                        decimalScale={3}
                      />
                    )}
                  </form.AppField>
                </div>

                <div className="md:col-span-1">
                  <form.AppField name="activityStatementFormat">
                    {(field) => (
                      <field.SelectField label="Activity statement format">
                        <field.SelectItem value="XERO">Xero</field.SelectItem>
                        <field.SelectItem value="NETSUITE">
                          NetSuite
                        </field.SelectItem>
                      </field.SelectField>
                    )}
                  </form.AppField>
                </div>

                <div className="md:col-span-2">
                  <form.AppField name="xeroContactId">
                    {(field) => (
                      <field.InputField
                        type="text"
                        placeholder="5cf3a0ee-6c7a-42a9-8a2e-c70428e68922"
                        label=<span className="flex flex-row gap-1">
                          Xero Contact ID
                          <Tooltip>
                            <TooltipTrigger type="button">
                              <FaInfoCircle />
                            </TooltipTrigger>
                            <TooltipContent>
                              <div className="flex max-w-96 flex-col gap-2">
                                <Txt size="xs" className="text-inherit">
                                  Used for invoicing.
                                </Txt>
                                <Txt size="xs" className="text-inherit">
                                  Can be found by going to{" "}
                                  <a
                                    href="https://go.xero.com/app/!Fy3HT/contacts/all"
                                    target="_blank"
                                  >
                                    Contacts
                                  </a>
                                  , selecting the contact, then copying the ID
                                  from the URL.
                                </Txt>
                                <Txt size="xs" className="text-inherit">
                                  Will be a string like:{" "}
                                  <span className="text-nowrap font-mono">
                                    cf3a0ee-6c7a-42a9-8a2e-c70428e68922
                                  </span>{" "}
                                  after{" "}
                                  <span className="font-mono">/contact</span>)
                                </Txt>
                              </div>
                            </TooltipContent>
                          </Tooltip>
                        </span>
                      />
                    )}
                  </form.AppField>
                </div>

                <div className="mt-4 flex w-full flex-row justify-end md:col-span-3">
                  <form.Subscribe selector={(state) => state.isDirty}>
                    {(isDirty) => (
                      <form.SubmitButton
                        disabled={!isDirty || loading}
                        childrenWhileSubmitting="Saving..."
                      >
                        Save
                      </form.SubmitButton>
                    )}
                  </form.Subscribe>
                </div>
              </form>
            </form.AppForm>
          )}
        </Card>

        <Card className="p-8">
          <button
            className="flex w-full flex-row items-center justify-between gap-2 text-left"
            onClick={() => setApprovalsCollapsed((prev) => !prev)}
            aria-label="Toggle approval policies"
          >
            <div className="flex items-center gap-2 font-semibold text-xl">
              <PiCheckCircle />
              Approval policies
            </div>
            <div
              className={cn(
                "transition-transform duration-300",
                approvalsCollapsed ? "rotate-0" : "rotate-180",
              )}
            >
              <RiArrowDownSLine className="size-5" />
            </div>
          </button>
          {!approvalsCollapsed && (
            <div className="flex flex-row gap-4 pt-4">
              {policyDefinitions.map((definition) => (
                <ApprovalPolicyCard
                  organisationId={organisation.id}
                  key={definition.kind}
                  definition={definition}
                />
              ))}
            </div>
          )}
        </Card>

        <Card className="p-8">
          <button
            className="flex w-full flex-row items-center justify-between gap-2 text-left"
            onClick={() => setUsersCollapsed((prev) => !prev)}
            aria-label="Toggle users"
          >
            <div className="flex items-center gap-2 font-semibold text-xl">
              <PiUser />
              Users ({organisation.memberships?.length ?? 0})
            </div>
            <div
              className={cn(
                "transition-transform duration-300",
                usersCollapsed ? "rotate-0" : "rotate-180",
              )}
            >
              <RiArrowDownSLine className="size-5" />
            </div>
          </button>
          {!usersCollapsed && (
            <div className="mt-4">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b text-left">
                    <th className="pb-2 font-medium">User</th>
                    <th className="pb-2 font-medium">Notifications</th>
                  </tr>
                </thead>
                <tbody>
                  {(organisation.memberships ?? [])
                    .filter(
                      (m) =>
                        m.userProfile !== null && m.userProfile !== undefined,
                    )
                    .map((membership) => {
                      const userProfile = membership.userProfile;
                      if (!userProfile) return null;
                      return (
                        <tr
                          key={membership.id}
                          className="border-b last:border-0"
                        >
                          <td className="py-2 pr-4">
                            <UserProfile {...userProfile} />
                          </td>
                          <td className="py-2">
                            <div className="flex items-center gap-2">
                              <Switch
                                checked={
                                  userProfile.notificationsEnabled ?? true
                                }
                                disabled={notificationToggleLoading}
                                onCheckedChange={async (checked) => {
                                  notificationLoadingUserIdRef.current =
                                    userProfile.id;
                                  try {
                                    await setUserNotificationsEnabled({
                                      variables: {
                                        input: {
                                          userProfileId: userProfile.id,
                                          notificationsEnabled: checked,
                                        },
                                      },
                                    });
                                  } catch {
                                    toast({
                                      kind: "error",
                                      title:
                                        "Failed to update notification preference",
                                    });
                                  }
                                }}
                                aria-label={`Toggle notifications for ${userProfile.firstName ?? userProfile.email}`}
                              />
                              {notificationToggleLoading &&
                                notificationLoadingUserIdRef.current ===
                                  userProfile.id && (
                                  <Spinner className="size-4" />
                                )}
                              {notificationData?.setUserNotificationsEnabled
                                .userProfile.id === userProfile.id &&
                                !notificationToggleLoading && (
                                  <PiCheckCircle className="size-4 text-success-600" />
                                )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                </tbody>
              </table>
            </div>
          )}
        </Card>

        <Card className="p-8">
          <button
            className="flex w-full flex-row items-center justify-between gap-2 text-left"
            onClick={() => setRulesCollapsed((prev) => !prev)}
            aria-label="Toggle rules"
          >
            <div className="flex items-center gap-2 font-semibold text-xl">
              <PiRobot />
              Rules (
              {organisation.assetRulesSummary?.activeAssetRules.length ?? 0})
            </div>
            <div
              className={cn(
                "transition-transform duration-300",
                rulesCollapsed ? "rotate-0" : "rotate-180",
              )}
            >
              <RiArrowDownSLine className="size-5" />
            </div>
          </button>
          {!rulesCollapsed && (
            <div className="mt-4">
              <DataTable
                columns={assetRuleColumns}
                data={organisation.assetRulesSummary?.activeAssetRules ?? []}
                hidePagination
                showCsvExport={false}
                emptyState={
                  <Txt color="secondary" size="sm">
                    No rules configured
                  </Txt>
                }
              />
            </div>
          )}
        </Card>

        <div className="relative">
          <div className="absolute top-4 right-4 z-10 flex items-center gap-2 rounded-md bg-white/80 px-2 py-1">
            <Txt size="xs">Include inactive accounts</Txt>
            <Switch
              checked={includeInactiveAssets}
              onCheckedChange={(checked) => {
                setIncludeInactiveAssets(checked);
                setAccountsFilter((curr) => ({
                  ...curr,
                  includeInactive: checked,
                }));
              }}
              aria-label="Toggle include inactive accounts"
            />
          </div>
          <LoadingToastProvider animationUrl={loadingAnimation}>
            <AccountList
              collapseSidebar
              filter={accountsFilter}
              orderBy={accountsOrderBy}
              groupKind={accountsGroup}
              loading={accountListLoading}
              onFilterChange={setAccountsFilter}
              onOrderByChange={setAccountsOrderBy}
              onGroupKindChange={setAccountsGroup}
              title={<>Total value of accounts ({organisation.homeCurrency})</>}
              headlineChart="balanceTimeSeries"
              organisation={accountListOrganisation}
              accountsSummary={accountListSummary}
              assetLinkPrefixOverride="/assets"
              beneficiaryLinkPrefixOverride="/beneficiary-accounts"
              hideFavouriteColumn
              TreasuryIcon={RiBankLine}
            />
          </LoadingToastProvider>
        </div>

        <Card className="border border-error-200 bg-error-50/40 p-8">
          <div className="flex flex-col gap-6 md:flex-row md:items-center md:justify-between">
            <div className="flex flex-col gap-2">
              <Heading
                as="h2"
                size="lg"
                className="font-semibold text-error-600"
              >
                Danger zone
              </Heading>
              <Txt size="sm" className="text-error-700">
                Deactivating an organisation prevents further activity and
                access in the admin portal.
              </Txt>
            </div>
            <Dialog>
              <DialogTrigger asChild>
                <Button variant="destructive">Deactivate organisation</Button>
              </DialogTrigger>
              <DialogContent>
                <MfaFlow
                  promptVariant="dialog-content"
                  onResolve={handleDeactivateOrganisation}
                >
                  {(requestMfa) => (
                    <>
                      <DialogHeader>
                        <DialogTitle>Deactivate organisation</DialogTitle>
                        <DialogDescription>
                          This action disables the organisation and cannot be
                          undone from this page.
                        </DialogDescription>
                      </DialogHeader>
                      <form
                        className="flex flex-col gap-6"
                        onSubmit={async (event) => {
                          event.preventDefault();
                          await requestMfa();
                        }}
                      >
                        <DialogFooter className="flex items-center gap-2">
                          <DialogClose asChild>
                            <Button type="button" variant="secondary">
                              Cancel
                            </Button>
                          </DialogClose>
                          <Button
                            type="submit"
                            variant="destructive"
                            disabled={deactivateLoading}
                          >
                            {deactivateLoading
                              ? "Deactivating..."
                              : "Deactivate organisation"}
                          </Button>
                        </DialogFooter>
                      </form>
                    </>
                  )}
                </MfaFlow>
              </DialogContent>
            </Dialog>
          </div>
        </Card>
      </div>
    </AdminPageContainer>
  );
}

const newBusinessEntityFormSchema = (requiresNetsuiteCustomerId: boolean) =>
  z
    .object({
      name: z.string().min(1, "Please enter a name"),
      netsuiteInternalCustomerId: z.string().optional(),
    })
    .superRefine((value, ctx) => {
      if (
        requiresNetsuiteCustomerId &&
        !value.netsuiteInternalCustomerId?.trim()
      ) {
        ctx.addIssue({
          path: ["netsuiteInternalCustomerId"],
          code: z.ZodIssueCode.custom,
          message:
            "This organisation is configured to use NetSuite activity statements. A NetSuite internal customer ID is required for all business entities.",
        });
      }
    });
function NewBusinessEntityDialogTrigger({
  children,
  organisationId,
  organisationName,
  organisationActivityStatementFormat,
  onCreated,
}: {
  children: ReactNode;
  onCreated?: (newId: string) => void;
  organisationId: string;
  organisationName: string;
  organisationActivityStatementFormat?: ActivityStatementFormat;
}) {
  const [createBusinessEntity] = useMutation(
    graphql(`
    mutation CreateBusinessEntity(
      $name: String!
      $organisationId: ID!
      $netsuiteInternalCustomerId: ID
    ) {
      createBusinessEntity(
        input: {
          name: $name
          organisationId: $organisationId
          netsuiteInternalCustomerId: $netsuiteInternalCustomerId
        }
      ) {
        businessEntity {
          id
          name
        }
      }
    }`),
  );
  const [open, setOpen] = useState(false);
  const requiresNetsuiteCustomerId =
    organisationActivityStatementFormat === "NETSUITE";
  const form = useAppForm({
    onSubmit: async ({ value }) => {
      const result = await createBusinessEntity({
        variables: {
          name: value.name,
          organisationId,
          netsuiteInternalCustomerId: requiresNetsuiteCustomerId
            ? value.netsuiteInternalCustomerId?.trim()
            : undefined,
        },
      });

      if (!result.data) {
        throw Error("Failed to create new business entity");
      }
      setOpen(false);
      onCreated?.(result.data.createBusinessEntity.businessEntity!.id);
    },
    validators: {
      onSubmit: newBusinessEntityFormSchema(requiresNetsuiteCustomerId),
    },
    defaultValues: {
      name: "",
      netsuiteInternalCustomerId: "",
    } as z.input<ReturnType<typeof newBusinessEntityFormSchema>>,
  });

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{children}</DialogTrigger>
      <DialogContent asChild>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            e.stopPropagation();
            void form.handleSubmit();
          }}
          className="grid gap-6"
        >
          <form.AppForm>
            <DialogHeader>
              <BreadcrumbArray
                stack={[
                  {
                    title: organisationName,
                  },
                ]}
              />
              <DialogTitle>New business entity</DialogTitle>
              <DialogDescription className="sr-only">
                Use this form to create a new business entity
              </DialogDescription>
            </DialogHeader>
            <form.AppField name="name">
              {(field) => (
                <field.InputField
                  label="Name"
                  placeholder="Enter a name for the new business entity"
                />
              )}
            </form.AppField>
            {requiresNetsuiteCustomerId && (
              <form.AppField name="netsuiteInternalCustomerId">
                {(field) => (
                  <field.InputField
                    label="NetSuite internal customer ID"
                    placeholder={
                      requiresNetsuiteCustomerId
                        ? "Required for this organisation"
                        : "Optional for this organisation"
                    }
                  />
                )}
              </form.AppField>
            )}
            <DialogFooter>
              <form.SubmitButton childrenWhileSubmitting="Creating business entity...">
                Create business entity
              </form.SubmitButton>
            </DialogFooter>
          </form.AppForm>
        </form>
      </DialogContent>
    </Dialog>
  );
}

const ApprovalPolicyCard = ({
  definition,
  organisationId,
}: {
  definition: ApprovalPolicy;
  organisationId: string;
}) => {
  const [setOrganisationApprovalPolicies, { loading }] = useMutation(
    graphql(`
        mutation SetOrganisationApprovalPoliciesForAdmin(
          $input: SetOrganisationApprovalPoliciesInput!
        ) {
          setOrganisationApprovalPolicies(input: $input) {
            approvalPolicies {
              id
              allowPolicyOverride
              allowSelfReview
              numUserReviewsRequired
            }
          }
        }
      `),
  );

  const form = useAppForm({
    defaultValues: {
      kind: definition.kind,
      numUserReviewsRequired: String(
        definition.approvalPolicy?.numUserReviewsRequired ?? 0,
      ) as ApprovalPolicyFormValues["numUserReviewsRequired"],
      allowSelfReview: definition.approvalPolicy?.allowSelfReview ?? false,
      allowPolicyOverride:
        definition.approvalPolicy?.allowPolicyOverride ?? false,
    },
    validators: {
      onSubmit: approvalPolicyFormSchema,
    },
    onSubmit: async ({ value }) => {
      const parsed = approvalPolicyFormSchema.safeParse(value);
      if (!parsed.success) {
        toast({
          kind: "error",
          title: "Please fix the validation errors",
        });
        return;
      }

      await setOrganisationApprovalPolicies({
        variables: {
          input: {
            organisationId,
            approvalPolicies: [
              {
                kind: parsed.data.kind,
                numUserReviewsRequired: Number(
                  parsed.data.numUserReviewsRequired,
                ),
                allowPolicyOverride: !!parsed.data.allowPolicyOverride,
                allowSelfReview: !!parsed.data.allowSelfReview,
              },
            ],
          },
        },
        onCompleted: () => {
          toast({
            kind: "success",
            title: "Approval policy saved",
          });
        },
        onError: (error) => {
          toast({
            kind: "error",
            title: "Failed to save approval policy",
            description:
              error.graphQLErrors?.[0]?.message ?? error.message ?? undefined,
          });
        },
      });
    },
  });

  const approvalPolicyExists = !!definition.approvalPolicy;
  return (
    <div className="flex-[1] overflow-hidden">
      <form
        onSubmit={(event) => {
          event.preventDefault();
          event.stopPropagation();
          void form.handleSubmit();
        }}
        className="flex flex-col gap-4"
      >
        <form.AppForm>
          <div className="font-semibold text-lg">
            {prettyPrintEnum(definition.kind)}
          </div>
          <div className="flex flex-col gap-6">
            {!approvalPolicyExists && (
              <Alert
                kind="warning"
                title="No approval policy"
                description={`This organisation does not have an approval policy. ${prettyPrintEnum(definition.kind)} functionality will not work until this is resolved. Create an approval policy using the form below.`}
              />
            )}

            <form.AppField name="numUserReviewsRequired">
              {(field) => (
                <field.SelectField label="Number of user reviews required">
                  {["0", "1", "2", "3", "4"].map((value) => (
                    <field.SelectItem key={value} value={value}>
                      {value}
                    </field.SelectItem>
                  ))}
                </field.SelectField>
              )}
            </form.AppField>

            <form.AppField name="allowSelfReview">
              {(field) => (
                <field.SwitchField
                  label="Allow admins to self-review"
                  description={
                    <p className="text-secondary text-xs">{`When enabled, approvable ${prettyPrintEnum(
                      definition.kind,
                    )} created by organisation admins will automatically receive an approval from the user who created them.`}</p>
                  }
                />
              )}
            </form.AppField>

            <form.AppField name="allowPolicyOverride">
              {(field) => (
                <field.SwitchField
                  label="Allow admins to override review requirements"
                  description={
                    <p className="text-secondary text-xs">{`When enabled, approvable ${prettyPrintEnum(definition.kind)} created by organisation admins will be considered fully approved, regardless of how many approvals are required by this policy.`}</p>
                  }
                />
              )}
            </form.AppField>

            <div className="flex">
              <form.SubmitButton
                size="sm"
                disabled={loading}
                childrenWhileSubmitting={
                  approvalPolicyExists ? "Saving..." : "Creating..."
                }
              >
                {approvalPolicyExists ? "Save changes" : "Create policy"}
              </form.SubmitButton>
            </div>
          </div>
        </form.AppForm>
      </form>
    </div>
  );
};
